// Which transcript blocks belong to a Jira artifact, and how a drill-in on one lands in
// the "View by" filter. Split out of sessions.ts for the same reason breakdownRow.ts is
// split out of costArtifact.ts: this is pure, so it can be unit-tested directly, while
// its home module installs document listeners at load and can't be imported under Node.
//
// The problem it solves: a cost bar says AL-111 earned $110, you open a session behind it,
// and you land on turn 1 of 241 blocks with no idea which turns that money paid for. The
// panel buckets money by block→artifact attribution; this reads the SAME attribution back
// so the transcript can show exactly those turns.
//
// An epic is the awkward case. Every session has at most ONE epic, so an epic can't be a
// filter dimension of its own — one chip filters nothing. But an epic's money lives in its
// descendant tickets, so an epic drill-in resolves to the UNION of those, via the ancestor
// chain the server ships per block.

/** A block's labels, as much of them as focusing reads. */
export interface FocusBlock {
  idx: number
  /** The Jira item this block was charged to; `anc` = its ancestors, nearest-first. */
  ticket?: { id: string; ident?: string | null; anc?: string[] } | null
  feature?: { id: string; title?: string } | null
}

/** Prefix marking a filter value as "everything under this artifact", not one ticket. */
export const UNDER = 'under:'

/**
 * Does this block's work belong to `target` (an artifact id)?
 *
 * Three ways it can, and all three are load-bearing on real data:
 *   * the block's own ticket IS the target — a leaf drill-in;
 *   * the target is an ANCESTOR of the block's ticket — an epic or phase drill-in, which
 *     is the only way those ever match, since a parent rarely holds blocks itself;
 *   * the block was charged to the target as a FEATURE — a Jira epic is stored
 *     kind='feature', and some sessions attribute straight to the epic with no ticket at
 *     all (one real session: 105 blocks, zero tickets). Without this arm those sessions
 *     would open unfocused while their sibling sessions focused fine.
 */
export function blkUnder(b: FocusBlock | undefined, target: string): boolean {
  if (!b || !target) return false
  if (b.ticket && (b.ticket.id === target || (b.ticket.anc || []).indexOf(target) >= 0)) return true
  return !!(b.feature && b.feature.id === target)
}

/** What a ticket/epic drill-in resolves to in the ticket dimension. */
export interface TicketFocus {
  /** The filter value: a bare artifact id, or `under:<id>` for a roll-up. */
  key: string
  /** True when `key` is a union over descendants rather than one ticket. */
  roll: boolean
  /** How many distinct tickets the union covers (1 for an exact hit). */
  n: number
  /** The sole member's key when the union has exactly one, else null. */
  only: string | null
}

/**
 * How `target` lands in the ticket dimension — or null when this session has no work
 * attributed to it, in which case the caller must leave the transcript unfiltered rather
 * than focus on nothing and show a blank pane.
 *
 * Pure, so it doubles as a "would this focus anything?" predicate when wiring chips.
 *
 * The WIDER answer wins whenever the target has attributed descendants: an exact chip is
 * returned only when the target's own blocks account for everything under it. A parent
 * whose children hold some of the money reads as the union, because that union is what
 * its bar in the cost panel measures — an exact chip there would hide the very blocks
 * that earned part of the number the user clicked.
 */
export function ticketFocus(blocks: FocusBlock[], target: string): TicketFocus | null {
  if (!target) return null
  const covered = blocks.filter((b) => blkUnder(b, target))
  if (!covered.length) return null
  const exact = blocks.filter((b) => b.ticket && b.ticket.id === target).length
  if (exact && exact === covered.length) return { key: target, roll: false, n: 1, only: null }
  const seen: Record<string, 1> = {}
  const idents: string[] = []
  for (const b of covered) {
    if (!b.ticket || seen[b.ticket.id]) continue
    seen[b.ticket.id] = 1
    idents.push(b.ticket.ident || b.ticket.id)
  }
  return { key: UNDER + target, roll: true, n: idents.length, only: idents.length === 1 ? idents[0] ?? null : null }
}

/**
 * Does block `bi` match dimension `dim`'s selected value? `null` = All.
 *
 * The one place roll-up values are understood, so every consumer — the filter itself, the
 * chip sparkline, the "··· N turns elsewhere ···" gaps — agrees on which blocks a value
 * covers. They disagreed once and the sparkline showed an empty bar for a live filter.
 */
export function blkMatch(
  blocks: FocusBlock[],
  bi: number,
  dim: string,
  val: string | null,
  blkVal: (bi: number, dim: string) => string | null,
): boolean {
  if (val == null) return true
  if (dim === 'ticket' && val.indexOf(UNDER) === 0) return blkUnder(blocks[bi], val.slice(UNDER.length))
  return blkVal(bi, dim) === val
}

/**
 * Whether dimension `dim` with `nValues` distinct values is worth offering as a control.
 *
 * ≥2 for most dimensions: a lone PR or feature spans the session, so filtering to it
 * hides nothing. Tickets are the measured exception — across the corpus a single-ticket
 * session still leaves a median 20% of its blocks unattributed, so one ticket chip does
 * hide real turns and belongs on screen.
 */
export function dimIsUseful(dim: string, nValues: number, hasRollup?: boolean): boolean {
  // A roll-up keeps the ticket dimension alive even with NO ticket values, which is the
  // epic-only session: 33 blocks charged straight to the epic as a `feature` and not one
  // ticket row among them. The union is real and worth filtering to; without this the
  // dimension was dropped, the resolved focus had nowhere to live, and the drawer fell
  // through to Work type and opened at turn 1 — exactly the sessions the feature arm of
  // `blkUnder` exists to serve.
  if (dim === 'ticket' && hasRollup) return true
  return nValues >= (dim === 'ticket' ? 1 : 2)
}

/**
 * The roll-up chip's label, named after the artifact the user CLICKED.
 *
 * That name has to be passed in: the block payload never carries it, because a parent
 * with no blocks of its own contributes no label to read it from.
 *
 * `n === 0` is the epic-only session: blocks charged straight to the epic as a `feature`,
 * with no ticket rows beneath them to name. There is nothing to roll UP to, so the chip
 * is just the epic — "AL-106 ▸ all 0" would be both nonsense and a lie about a union that
 * covers 33 real blocks.
 */
export function rollupLabel(tf: TicketFocus, label: string | null, id: string): string {
  const name = label || id
  if (!tf.n) return name
  return name + ' ▸ ' + (tf.only || 'all ' + tf.n)
}

/**
 * The label for the chip that clears the filter.
 *
 * "All" is honest for PRs, features and work type: every block carries a value on those
 * dimensions, so unfiltering and "all values" are the same set. On TICKETS they diverge —
 * a session's unattributed turns belong to no ticket at all, so "All" was read as "all the
 * ticket work" while actually showing the whole transcript, noise included. Naming what it
 * does removes the ambiguity without removing the escape hatch, which has to stay: when
 * `ticket` is the only available dimension it is the sole route back to the full session.
 */
export function unfilterLabel(dim: string): string {
  return dim === 'ticket' ? 'Whole session' : 'All'
}
